import React, { useState, useEffect } from 'react';
import SummaryApi from '../common';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import displayINRCurrency from '../helpers/displayCurrency';

const EditOrder = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { orderid, orderData: passedOrderData } = location.state || {};
    const [loading, setLoading] = useState(false);
    const [orderData, setOrderData] = useState({});
    const [editedOrderStatus, setEditedOrderStatus] = useState('');
    const [initialStatus, setInitialStatus] = useState('');
    const [customerOrders, setCustomerOrders] = useState([]);

    const encodeProductId = (id) => {
        if (!id) return '';
        return btoa(encodeURIComponent(id));
    };

    const orderStatusOptions = [
        'Pending',
        'Order placed',
        'Processing',
        'Order confirmed',
        'Order in transit',
        'Delivered',
        'Cancelled'
    ];

    const forwardStatusOptions = {
        'Pending': ['Order placed', 'Cancelled'],
        'Order placed': ['Processing', 'Cancelled'],
        'Processing': ['Order confirmed', 'Cancelled'],
        'Order confirmed': ['Order in transit', 'Cancelled'],
        'Order in transit': ['Delivered'],
        'Delivered': [],
        'Cancelled': []
    };

    useEffect(() => {
        if (passedOrderData) {
            // If order data is passed directly, use it
            setOrderData(passedOrderData);
            setEditedOrderStatus(passedOrderData.order_status || '');
            setInitialStatus(passedOrderData.order_status || '');
            
            // Fetch customer's all orders
            if (passedOrderData.customer_id) {
                fetchCustomerOrders(passedOrderData.customer_id);
            }
        } else if (orderid) {
            // If only orderid is passed, we need to fetch from all orders first
            fetchOrderFromList();
        } else {
            toast.error('No order data provided');
            navigate('/admin-panel/orders/all');
        }
    }, [orderid, passedOrderData]);

    const fetchOrderFromList = async () => {
        setLoading(true);
        try {
            // First get all orders to find this order
            const response = await fetch(SummaryApi.allOrder.url, {
                method: SummaryApi.allOrder.method,
                headers: {
                    'Authorization': SummaryApi.bearerToken,
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch orders: ${response.status}`);
            }

            const result = await response.json();
            console.log('All orders response:', result);

            // Find the specific order
            let order = null;
            if (result.status && result.data && Array.isArray(result.data)) {
                order = result.data.find(o => o.order_id == orderid);
            } else if (Array.isArray(result)) {
                order = result.find(o => o.order_id == orderid);
            }

            if (order) {
                console.log('Found order:', order);
                setOrderData(order);
                setEditedOrderStatus(order.order_status || '');
                setInitialStatus(order.order_status || '');
                
                // Fetch customer's all orders
                if (order.customer_id) {
                    fetchCustomerOrders(order.customer_id);
                }
            } else {
                toast.error('Order not found');
            }
        } catch (error) {
            console.error('Error fetching order:', error);
            toast.error('Error fetching order: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchCustomerOrders = async (customerId) => {
        try {
            console.log('Fetching orders for customer_id:', customerId);
            
            const response = await fetch(SummaryApi.orderDetailsByOrderID.url, {
                method: SummaryApi.orderDetailsByOrderID.method,
                headers: {
                    'Authorization': SummaryApi.bearerToken,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ customer_id: customerId }),
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch customer orders: ${response.status}`);
            }

            const responseData = await response.json();
            console.log('Customer orders response:', responseData);

            if (responseData.status) {
                // If it returns all customer orders
                const orders = responseData.data || responseData;
                setCustomerOrders(Array.isArray(orders) ? orders : [orders]);
                
                // If we didn't have order data yet, find the specific order
                if (!orderData.order_id && orderid) {
                    const specificOrder = Array.isArray(orders) 
                        ? orders.find(o => o.order_id == orderid)
                        : orders;
                    if (specificOrder) {
                        setOrderData(specificOrder);
                        setEditedOrderStatus(specificOrder.order_status || '');
                        setInitialStatus(specificOrder.order_status || '');
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching customer orders:', error);
            // Don't show toast as this is secondary data
        }
    };

    const handleOrderStatusChange = (e) => {
        const newStatus = e.target.value;
        if (forwardStatusOptions[editedOrderStatus]?.includes(newStatus) || 
            newStatus === 'Cancelled' || 
            newStatus === editedOrderStatus) {
            setEditedOrderStatus(newStatus);
        }
    };

    const handleSubmit = async () => {
        if (!orderData.order_id) {
            toast.error('No order data available');
            return;
        }

        if (editedOrderStatus === initialStatus) {
            toast.info('No changes detected');
            return;
        }

        setLoading(true);
        try {
            console.log('Updating order status:', {
                order_id: orderData.order_id,
                order_status: editedOrderStatus
            });

            const response = await fetch(SummaryApi.editOrderStatus.url, {
                method: SummaryApi.editOrderStatus.method,
                headers: {
                    'Authorization': SummaryApi.bearerToken,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    order_id: orderData.order_id,
                    order_status: editedOrderStatus,
                }),
            });

            const responseData = await response.json();
            console.log('Update response:', responseData);
            
            if (response.ok && responseData.status) {
                toast.success('Order status updated successfully!');
                setInitialStatus(editedOrderStatus);
                setOrderData(prev => ({
                    ...prev,
                    order_status: editedOrderStatus
                }));
                
                // Refresh customer orders to show updated status
                if (orderData.customer_id) {
                    fetchCustomerOrders(orderData.customer_id);
                }
            } else {
                toast.error(responseData.message || 'Failed to update order status. Please try again.');
            }
        } catch (error) {
            console.error('Error updating order status:', error);
            toast.error('An error occurred while updating the order status: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString || dateString === '0000-00-00' || dateString === '0000-00-00 00:00:00') {
            return 'Not yet delivered';
        }
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return 'Invalid Date';
        }
        const optionsDate = { day: '2-digit', month: 'short', year: 'numeric' };
        const optionsTime = { hour: '2-digit', minute: '2-digit', hour12: true };
        return `${date.toLocaleDateString('en-US', optionsDate)} ${date.toLocaleTimeString('en-US', optionsTime)}`;
    };

    const navigateToAllOrders = () => {
        navigate('/admin-panel/orders/all');
    };

    const navigateToInvoice = () => {
        if (orderData.order_id) {
            const encodedId = btoa(encodeURIComponent(orderData.order_id.toString()));
            navigate(`/admin-panel/orders/invoice/${encodedId}`, { state: { order: orderData } });
        } else {
            toast.error('Order ID not available');
        }
    };

    const getAllowedStatuses = () => {
        if (!editedOrderStatus) return orderStatusOptions;
        
        const allowed = [editedOrderStatus];
        if (forwardStatusOptions[editedOrderStatus]) {
            allowed.push(...forwardStatusOptions[editedOrderStatus]);
        }
        if (editedOrderStatus !== 'Cancelled' && editedOrderStatus !== 'Delivered') {
            allowed.push('Cancelled');
        }
        
        return [...new Set(allowed)];
    };

    const getCustomerInitials = (name) => {
        if (!name) return 'C';
        const parts = name.split(' ');
        if (parts.length > 1) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return name.charAt(0).toUpperCase();
    };

    // Filter out current order from customer orders
    const otherCustomerOrders = customerOrders.filter(order => 
        order.order_id != orderData.order_id
    );

    return (
        <div className="relative mx-auto p-4">
            {loading && (
                <div className='absolute top-0 left-0 right-0 bottom-0 bg-white bg-opacity-75 flex justify-center items-center z-50'>
                    <div className='text-lg font-bold'>Loading...</div>
                </div>
            )}

            <div className="flex justify-end mb-4 space-x-2">
                <button
                    onClick={navigateToAllOrders}
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-xs md:text-sm"
                >
                    All Orders
                </button>
                <button
                    onClick={navigateToInvoice}
                    className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded text-xs md:text-sm"
                >
                    View Invoice
                </button>
            </div>
            
            <div className="flex flex-col lg:flex-row lg:space-x-6">
                <div className="w-full lg:w-1/4 flex flex-col space-y-6 mb-6 lg:mb-0">
                    <div className="bg-white p-6 rounded-lg shadow-md border">
                        <div className="flex items-center space-x-4">
                            <div className="h-16 w-16 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-xl">
                                {getCustomerInitials(orderData.customer_name)}
                            </div>
                            <div>
                                <h2 className="text-lg md:text-xl font-semibold text-gray-800">
                                    {orderData.customer_name || 'N/A'}
                                </h2>
                                <p className="text-xs md:text-sm text-gray-600">
                                    {orderData.email || 'N/A'}
                                </p>
                                <p className="text-xs md:text-sm text-gray-600">
                                    {orderData.mobile_number || 'N/A'}
                                </p>
                                {/* <p className="text-xs text-gray-500 mt-1">
                                    Customer ID: {orderData.customer_id || 'N/A'}
                                </p> */}
                            </div>
                        </div>
                        
                        {/* Customer's Other Orders */}
                        {otherCustomerOrders.length > 0 && (
                            <div className="mt-4">
                                <h4 className="font-semibold text-gray-700 mb-2 text-sm">Other Orders:</h4>
                                <div className="space-y-2 max-h-40 overflow-y-auto">
                                    {otherCustomerOrders.slice(0, 5).map((order, index) => (
                                        <div key={index} className="p-2 bg-gray-50 rounded text-xs">
                                            <p className="font-medium truncate">{order.order_code}</p>
                                            <p className="text-gray-500">
                                                {formatDateTime(order.order_date || order.order_date_time).split(',')[0]}
                                            </p>
                                            <p className="mt-1">
                                                Status: <span className={`px-1 py-0.5 rounded text-xs ${
                                                    order.order_status === 'Delivered' ? 'bg-green-100 text-green-800' :
                                                    order.order_status === 'Cancelled' ? 'bg-red-100 text-red-800' :
                                                    'bg-yellow-100 text-yellow-800'
                                                }`}>
                                                    {order.order_status}
                                                </span>
                                            </p>
                                        </div>
                                    ))}
                                    {otherCustomerOrders.length > 5 && (
                                        <p className="text-xs text-gray-500 text-center">
                                            +{otherCustomerOrders.length - 5} more orders
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    
                    <div className="bg-white p-6 rounded-lg shadow-md border flex-1">
                        <h3 className="text-lg md:text-xl font-semibold text-gray-800 mb-2">Order Address:</h3>
                        <p className="text-xs md:text-sm text-gray-600 mb-4">
                            {orderData.order_address || 'No address provided'}
                        </p>
                        {(orderData.latitude && orderData.longitude) && (
                            <div className="mt-2 h-64">
                                <iframe
                                    src={`https://www.google.com/maps?q=${orderData.latitude},${orderData.longitude}&z=15&output=embed`}
                                    width="100%"
                                    height="100%"
                                    allowFullScreen
                                    loading="lazy"
                                    title="Order Address"
                                    className="border rounded"
                                />
                            </div>
                        )}
                    </div>
                </div>
                
                <div className="flex-1 lg:flex lg:flex-col lg:space-y-6">
                    <div className="bg-white p-6 rounded-lg shadow-md border mb-6 lg:mb-0 flex-1">
                        <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4 border-b">Order Details</h2>
                        <div className="gap-4">
                            <div className="mb-4">
                                <p className="flex justify-between text-xs md:text-base text-gray-700 my-3">
                                    <strong>Order Code:</strong> 
                                    <span className="font-semibold">{orderData.order_code || 'N/A'}</span>
                                </p>
                                <p className="flex justify-between text-xs md:text-base text-gray-700 my-3">
                                    <strong>Order Date:</strong> 
                                    <span className="font-semibold">
                                        {formatDateTime(orderData.order_date_time || orderData.order_date)}
                                    </span>
                                </p>
                                {/* <p className="flex justify-between text-xs md:text-base text-gray-700 my-3">
                                    <strong>Order ID:</strong> 
                                    <span className="font-semibold">{orderData.order_id || 'N/A'}</span>
                                </p> */}
                                <p className="flex justify-between text-xs md:text-base text-gray-700 my-3">
                                    <strong>Payment Mode:</strong> 
                                    <span className="font-semibold">{orderData.payment_mode || 'N/A'}</span>
                                </p>
                                <p className="flex justify-between text-xs md:text-base text-gray-700 my-3">
                                    <strong>Current Status:</strong> 
                                    <span className="font-semibold">
                                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                            orderData.order_status === 'Pending' ? 'bg-yellow-200 text-yellow-800' :
                                            orderData.order_status === 'Order placed' ? 'bg-orange-200 text-orange-800' :
                                            orderData.order_status === 'Processing' ? 'bg-purple-200 text-purple-800' :
                                            orderData.order_status === 'Order confirmed' ? 'bg-blue-200 text-blue-800' :
                                            orderData.order_status === 'Order in transit' ? 'bg-teal-200 text-teal-800' :
                                            orderData.order_status === 'Delivered' ? 'bg-green-200 text-green-800' :
                                            orderData.order_status === 'Cancelled' ? 'bg-red-200 text-red-800' :
                                            'bg-gray-200 text-gray-800'
                                        }`}>
                                            {orderData.order_status || 'N/A'}
                                        </span>
                                    </span>
                                </p>
                            </div>
                            <div className="p-4 border border-gray-200 rounded-lg bg-yellow-50">
                                <p className="flex justify-between text-xs md:text-base text-gray-700 my-3">
                                    <strong>Order Amount:</strong> 
                                    <span className="font-semibold">
                                        {displayINRCurrency(orderData.order_amount || 0)}
                                    </span>
                                </p>
                                <p className="flex justify-between text-xs md:text-base text-gray-700 my-3">
                                    <strong>Delivery Charges:</strong>
                                    <span className="font-semibold">
                                        {displayINRCurrency(orderData.delivery_charges || 0)}
                                    </span>
                                </p>
                                <p className="flex justify-between text-xs md:text-base text-gray-700 my-3">
                                    <strong>GST Charges:</strong>
                                    <span className="font-semibold">
                                        {displayINRCurrency(orderData.GST || orderData.gst || 0)}
                                    </span>
                                </p>
                                <p className="flex justify-between text-xs md:text-base text-gray-700 my-3">
                                    <strong>Other Charges:</strong>
                                    <span className="font-semibold">
                                        {displayINRCurrency(orderData.other_charges || 0)}
                                    </span>
                                </p>
                                <p className="flex justify-between text-base md:text-lg text-gray-700 my-3 border-t pt-2">
                                    <strong>Total Amount:</strong>
                                    <span className="font-semibold text-red-600">
                                        {displayINRCurrency(orderData.total_amount || 0)}
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="bg-white p-6 rounded-lg shadow-md border flex-1">
                        <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4 border-b">Update Order Status</h2>
                        <div className="gap-4">
                            <p className="flex justify-between text-xs md:text-base text-gray-700 my-3">
                                <strong>Delivery Date:</strong> 
                                <span className="font-semibold">
                                    {formatDateTime(orderData.delivery_date)}
                                </span>
                            </p>
                            <div className="mt-4">
                                <label htmlFor="orderStatus" className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
                                    Update Order Status
                                </label>
                                <select
                                    id="orderStatus"
                                    className="mt-1 p-2 border border-gray-300 rounded-md w-full text-xs md:text-sm focus:outline-none focus:border-blue-500"
                                    value={editedOrderStatus}
                                    onChange={handleOrderStatusChange}
                                    disabled={initialStatus === 'Delivered' || initialStatus === 'Cancelled'}
                                >
                                    {getAllowedStatuses().map(status => (
                                        <option key={status} value={status}>
                                            {status}
                                        </option>
                                    ))}
                                </select>
                                <p className="text-xs text-gray-500 mt-2">
                                    Current status: <span className="font-semibold">{initialStatus}</span>
                                </p>
                            </div>
                            <div className="flex justify-end mt-4">
                                {initialStatus !== 'Delivered' && initialStatus !== 'Cancelled' && editedOrderStatus !== initialStatus && (
                                    <button
                                        id="submitBtn"
                                        onClick={handleSubmit}
                                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-xs md:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                        disabled={loading}
                                    >
                                        {loading ? 'Updating...' : 'Update Status'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md border mt-6">
                <h2 className="text-xl md:text-2xl font-semibold mb-4 text-gray-800 border-b">Items List</h2>
                {orderData.order_items && orderData.order_items.length > 0 ? (
                    <div className="flex flex-col space-y-4">
                        {orderData.order_items.map((item, index) => (
                            <div
                                key={item.order_items_id || index}
                                className="flex flex-col md:flex-row items-start md:items-center p-4 bg-white rounded-lg shadow-sm border relative"
                            >
                                <div className="flex items-center flex-1">
                                    <div className="h-24 w-24 bg-gray-100 border rounded-lg flex items-center justify-center mr-4">
                                        {item.product_img ? (
                                            <img
                                                src={SummaryApi.productImage.url + item.product_img}
                                                alt={item.product_name}
                                                className="h-full w-full object-contain p-2"
                                                onError={(e) => {
                                                    e.target.src = 'https://via.placeholder.com/96?text=Product';
                                                }}
                                            />
                                        ) : (
                                            <span className="text-gray-400 text-sm">No Image</span>
                                        )}
                                    </div>
                                    <div className="flex flex-col">
                                        <h4 className="text-base md:text-lg font-semibold">
                                            {item.product_name || 'Product Name'}
                                        </h4>
                                        <p className="text-xs md:text-sm text-gray-600">
                                            {displayINRCurrency(item.price || 0)} x {item.quantity || 0}
                                        </p>
                                        {item.warranty && (
                                            <p className="text-xs text-blue-600 mt-1">
                                                Warranty: {item.warranty}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <p className="text-base md:text-lg font-semibold mt-2 md:mt-0">
                                    {displayINRCurrency(item.subtotal || 0)}
                                </p>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-gray-600 text-xs md:text-sm">No items found in this order</p>
                )}
            </div>
        </div>
    );
};

export default EditOrder;